import React from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import MarketRegForm from "@/components/site/MarketRegForm";
import TestimonialsSections from "@/components/site/TestimonialsSections";
import { FeaturedHero } from "@/components/site/FeaturedHero";
import { HomeMeditations } from "@/components/site/HomeMeditations";
import { Youtube, Facebook, Instagram } from "lucide-react";

const FOUNDER = "https://customer-assets.emergentagent.com/job_spiritual-hub-115/artifacts/yk6e1k68_WhatsApp%20Image%202026-03-20%20at%2011.30.29%20AM.jpeg";

const SHIFT = [
  "A confidence that you can meditate",
  "An awareness that you can remain ‘thought-free’ for minutes that pass like seconds",
  "How to create an ‘instant mental shift’ in real life situations",
  "How to nurture your mind like a muscle",
  "A calm mind, not by control but by understanding",
];

const FAQS = [
  ["Is this beginner friendly?", "Yes."],
  ["Are the sessions live?", "Yes. All sessions are conducted live on Zoom."],
  ["Do I need prior meditation experience?", "No prior experience is needed."],
  ["Can international participants join?", "Yes."],
  ["How do I receive Zoom details?", "You receive them via email and WhatsApp after registration."],
  ["Which payment methods are available?", "Razorpay for India and PayPal for international participants."],
];

export default function HomePage() {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({behavior:"smooth"});

  return (
    <div data-testid="home-page" className="fade-up">
      {/* HOME PAGE HERO SECTION — Admin "Homepage Featured Offer" */}
      <FeaturedHero />

      {/* MEDITATIONS (Digital Meditations Admin data) */}
      <HomeMeditations />

      {/* TRUST STRIP */}
      <section className="border-y border-[#EBE5D9] bg-white/60">
        <div className="container-ic py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-[#5C5955]">
          {["Live Zoom Sessions","Beginner Friendly","Small Batches","India + Global Participants","Guided by Umesh Misra"].map(t=>(
            <span key={t} className="inline-flex items-center gap-2"><span className="text-terracotta">✓</span>{t}</span>
          ))}
        </div>
      </section>

      {/* WHAT IS iCHAITANYA */}
      <section className="container-ic py-16 sm:py-20">
        <div className="soft-card max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-center">What is iChaitanya</h2>
          <div className="mt-6 space-y-4 text-[#3D3633] leading-relaxed text-lg sm:text-xl text-center">
            <p>iChaitanya begins with a simple truth — everyone has already meditated.</p>
            <p>Meditation is simple.</p>
            <p>It is a natural state of stillness, awareness, and presence that already lives within.</p>
            <p>The guru is within.</p>
            <p>iChaitanya is a Saarthi — a guiding presence — on the journey of Dhyaan, gently leading towards inner realisation.</p>
            <p>It is a journey into silence, beyond the movement of the mind, where peace, strength, clarity, and creative power reveal themselves from within.</p>
            <p>A journey that uplifts life from the inside.</p>
          </div>
        </div>
      </section>

      {/* MEDITATION ENLIGHTENMENT */}
      <section className="container-ic py-16 sm:py-20">
        <h2 className="font-serif text-4xl sm:text-5xl">Meditation Enlightenment</h2>
        <p className="font-serif text-2xl sm:text-3xl mt-4 text-[#2D2422]">A significant shift begins within you</p>
        <p className="mt-3 text-[#5C5955]">In this 40-minute enlightenment, something familiar begins to return.</p>

        <ol className="mt-8 grid md:grid-cols-2 gap-4 max-w-4xl">
          {SHIFT.map((p,i)=>(
            <li key={i} className="soft-card !p-5 flex gap-4">
              <span className="text-terracotta text-2xl leading-none">•</span>
              <span className="text-[#3D3633] leading-relaxed">{p}</span>
            </li>
          ))}
        </ol>

        <div className="mt-12 grid lg:grid-cols-2 gap-8">
          <div className="soft-card bg-[#F3EFEA]">
            <h3 className="font-serif text-2xl">Your 40-Minute Enlightenment</h3>
            <p className="mt-3 text-[#3D3633]">All that is needed:</p>
            <ul className="mt-4 space-y-3 text-[#3D3633]">
              <li><span className="text-terracotta font-medium">• </span>Sit in a very well lit room/place</li>
              <li><span className="text-terracotta font-medium">• </span>Willingness to show up <span className="block ml-7 text-sm text-[#7E736E]">Exactly as you are.</span></li>
              <li><span className="text-terracotta font-medium">• </span>Openness <span className="block ml-7 text-sm text-[#7E736E]">To whatever you experience.</span></li>
            </ul>
          </div>

          <MarketRegForm programType="meditation_enlightenment" id="enlightenment-form"
            tid="home" submitTestId="enl-secure-btn" optionHeading="Choose Your Session" optionType="session" />
        </div>
      </section>

      {/* BLISS PATH PROGRAM */}
      <section className="container-ic py-16 sm:py-20">
        <div className="soft-card grid md:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl">Bliss Path Program</h2>
            <p className="mt-5 text-[#3D3633] text-lg leading-relaxed">A guided meditation journey for inner calm, emotional balance, positivity, and conscious living.</p>
          </div>
          <div>
            <p className="text-[#3D3633]">Designed for anyone seeking freedom from:</p>
            <ul className="mt-3 space-y-1 text-[#3D3633]">
              <li>• Constant overthinking</li>
              <li>• Anxiety</li>
              <li>• Negative thoughts &amp; Fears</li>
              <li>• Mental Restlessness</li>
              <li>• Depression</li>
            </ul>
            <ul className="mt-5 space-y-1 text-[#3D3633]">
              <li>• 7 Days Guided Meditation</li>
              <li>• Flexible Batch Timing</li>
              <li>• Beginner Friendly</li>
            </ul>
            <button onClick={()=>navigate("/bliss-path")} data-testid="explore-bliss" className="btn-primary mt-7">Explore Bliss Path</button>
          </div>
        </div>
      </section>

      {/* AURA NIGHT SECTION */}
      <section className="container-ic py-16 sm:py-20">
        <div className="rounded-[2rem] bg-[#4A3B32] text-[#EBE5D9] px-8 sm:px-14 py-14">
          <h2 className="font-serif text-4xl sm:text-5xl text-white">Aura – Night Meditation</h2>
          <p className="mt-5 text-[#D4C9BE] leading-relaxed max-w-3xl">A gentle 10-minute nightly meditation experience designed to help you release the day, quiet the mind, and reconnect with peace and positivity before tomorrow begins.</p>
          <p className="mt-6 font-serif text-3xl text-white">9:00 PM — 9:10 PM</p>
          <p className="text-[#D4C9BE]">Live on Zoom</p>
          <button onClick={()=>navigate("/aura-night")} data-testid="join-aura" className="btn-primary mt-7">Join Aura Night Meditation</button>
        </div>
      </section>

      {/* iC ONE-TO-ONE */}
      <section className="container-ic py-16 sm:py-20">
        <div className="soft-card text-center max-w-3xl mx-auto">
          <h2 className="font-serif text-4xl sm:text-5xl">iC One-to-One</h2>
          <p className="mt-5 text-[#3D3633] text-lg leading-relaxed">A deeply personal meditative experience for inner clarity, emotional balance and stillness.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a href="https://wa.me/919217639689" data-testid="onetoone-whatsapp" className="btn-ghost">WhatsApp</a>
            <button onClick={()=>navigate("/one-to-one")} data-testid="onetoone-book" className="btn-primary">Book a Call</button>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-ic py-16 sm:py-20">
        <h2 className="font-serif text-4xl sm:text-5xl">What People Are Saying</h2>
        <p className="mt-4 text-[#5C5955]">Real stories from people who transformed their meditation practice.</p>

        <div className="mt-10">
          <TestimonialsSections preview />
        </div>

        <div className="mt-10"><button onClick={()=>navigate("/testimonials")} data-testid="watch-stories" className="btn-primary">See all testimonials</button></div>
      </section>

      {/* ABOUT UMESH */}
      <section className="container-ic py-16 sm:py-20">
        <div className="soft-card grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4">
            <img src={FOUNDER} alt="Umesh Misra" className="w-full rounded-[1.75rem] object-cover aspect-[4/5] shadow-[0_12px_30px_rgba(74,59,50,0.1)]"/>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl sm:text-5xl">About Umesh Misra</h2>
            <p className="mt-2 text-sm uppercase tracking-[0.2em] text-terracotta">Saarthi – iChaitanya</p>
            <div className="mt-6 space-y-4 text-[#3D3633] leading-relaxed">
              <p>Umesh Misra's journey into meditation did not begin in an ashram, a retreat, or through years of formal spiritual study.</p>
              <p>It began quietly, one morning, in a park in Mumbai.</p>
              <p>During a regular morning walk, Umesh sat down for a few moments of silence. There was no special preparation, no expectation, no search for an experience. Just a simple pause.</p>
              <p>And then, something shifted.</p>
              <p>The usual movement of thoughts began to settle. The noise of the mind softened. Time seemed to disappear. For what felt like only a few moments, he entered a deep, effortless stillness — a space where nothing needed to be done, understood, or controlled.</p>
              <p>When he opened his eyes, nearly twenty-five minutes had passed.</p>
              <p>That moment became a turning point.</p>
              <p>It showed him that meditation was not something distant. It was already present within — waiting to be remembered.</p>
              <p>From that day onwards, Umesh's relationship with meditation deepened naturally. Silence, awareness, and inner stillness became not just practices, but a living part of his everyday life — as natural as breathing.</p>
              <p>Through iChaitanya, Umesh brings this lived experience as a Saarthi — guiding people back to the stillness, silence and awareness already alive within them.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="container-ic py-16 sm:py-20">
        <h2 className="font-serif text-4xl sm:text-5xl">FAQ</h2>
        <Accordion type="single" collapsible className="mt-8 max-w-3xl">
          {FAQS.map(([q,a], i)=>(
            <AccordionItem key={i} value={`faq-${i}`} className="border-[#EBE5D9]" data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-serif text-xl text-[#2D2422]">{q}</AccordionTrigger>
              <AccordionContent className="text-[#5C5955]">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="container-ic py-16 sm:py-20">
        <div className="rounded-[2rem] bg-[#4A3B32] text-[#EBE5D9] px-8 sm:px-14 py-14 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl text-white">Meditation is already within you.</h2>
          <p className="mt-4 text-[#D4C9BE]">We help you return to it consciously.</p>
          <div className="mt-8"><button onClick={()=>scrollTo("enlightenment-form")} data-testid="final-cta-btn" className="btn-primary">Join the Next BATCH</button></div>
        </div>
      </section>

      {/* Social links (per doc note: YouTube, Facebook, Instagram at bottom) */}
      <section className="container-ic pb-10">
        <div className="flex justify-center gap-5 text-[#5C5955]">
          <a href="https://www.youtube.com/@ichaitanya.official" target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="social-youtube" className="hover:text-terracotta transition"><Youtube size={22}/></a>
          <a href="https://www.facebook.com/profile.php?id=61574920415832" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook" className="hover:text-terracotta transition"><Facebook size={22}/></a>
          <a href="https://www.instagram.com/movement_ichaitanya/" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram" className="hover:text-terracotta transition"><Instagram size={22}/></a>
        </div>
      </section>
    </div>
  );
}
